(ns yardwerkz.web.booking.details
  "Step 4: Job details — photo upload, pets, terrain, access instructions."
  (:require
   [uix.core :refer [defui $ use-state use-callback]]
   [io.factorhouse.rfx.core :as rfx]
   [yardwerkz.web.store :as store]
   [yardwerkz.web.ui.components :as ui]
   [yardwerkz.web.ui.icons :as icons]))

(defui details-step [{:keys [on-next on-back]}]
  (let [dispatch (rfx/use-dispatch)
        saved-details (rfx/use-sub [::store/draft-details])
        [has-pets set-pets] (use-state (get saved-details :has-pets false))
        [terrain set-terrain] (use-state (get saved-details :terrain "flat"))
        [instructions set-instructions] (use-state (get saved-details :access-instructions ""))
        [photos set-photos] (use-state (get saved-details :photos []))

        persist!
        (use-callback
         (fn [p t i ph]
           (dispatch [::store/set-details {:has-pets p
                                           :terrain t
                                           :access-instructions i
                                           :photos ph}]))
         [dispatch])

        handle-files
        (use-callback
         (fn [e]
           (let [files (.. e -target -files)
                 new-photos (into photos
                                  (map (fn [i]
                                         (let [file (aget files i)]
                                           {:file file
                                            :preview-url (js/URL.createObjectURL file)
                                            :name (.-name file)}))
                                       (range (.-length files))))]
             (set-photos new-photos)
             (persist! has-pets terrain instructions new-photos)))
         [photos has-pets terrain instructions persist!])

        remove-photo
        (use-callback
         (fn [idx]
           (let [photo (nth photos idx)
                 new-photos (vec (concat (subvec photos 0 idx) (subvec photos (inc idx))))]
             (when (:preview-url photo)
               (js/URL.revokeObjectURL (:preview-url photo)))
             (set-photos new-photos)
             (persist! has-pets terrain instructions new-photos)))
         [photos has-pets terrain instructions persist!])

        terrain-options ["flat" "slight-slope" "steep" "mixed"]]

    ($ :div {:class "space-y-6"}
       ($ :h2 {:class "text-lg font-semibold text-gray-900"} "Job Details")

       ;; Photo Upload
       ($ :div
          ($ :label {:class "block text-sm font-medium text-gray-700 mb-2"} "Photos of the area")
          ($ :div {:class "grid grid-cols-4 gap-3"}
             ;; Upload button
             ($ :label {:class "flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded-xl hover:border-green-400 hover:bg-green-50 cursor-pointer transition-colors"}
                ($ icons/Upload {:size 24 :class "text-gray-400"})
                ($ :span {:class "text-xs text-gray-500 mt-1"} "Add photos")
                ($ :input {:type "file"
                           :accept "image/jpeg,image/png,image/webp,image/heic"
                           :multiple true
                           :class "hidden"
                           :on-change handle-files}))
             ;; Photo thumbnails
             (for [[idx photo] (map-indexed vector photos)]
               ($ :div {:key idx
                        :class "relative h-24 rounded-xl overflow-hidden group"}
                  ($ :img {:src (:preview-url photo)
                           :class "w-full h-full object-cover"})
                  ($ :button {:class "absolute top-1 right-1 p-1 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                              :on-click #(remove-photo idx)}
                     ($ icons/X {:size 12}))))))

       ;; Pets
       ($ :div
          ($ :label {:class "block text-sm font-medium text-gray-700 mb-2"} "Are there pets in the yard?")
          ($ :div {:class "flex space-x-3"}
             ($ :button {:class (str "flex items-center space-x-2 px-4 py-2 rounded-lg border-2 transition-colors "
                                     (if has-pets
                                       "border-green-500 bg-green-50 text-green-700"
                                       "border-gray-200 text-gray-600 hover:border-gray-300"))
                         :on-click (fn []
                                     (set-pets true)
                                     (persist! true terrain instructions photos))}
                ($ icons/Dog {:size 18})
                ($ :span "Yes"))
             ($ :button {:class (str "flex items-center space-x-2 px-4 py-2 rounded-lg border-2 transition-colors "
                                     (if (not has-pets)
                                       "border-green-500 bg-green-50 text-green-700"
                                       "border-gray-200 text-gray-600 hover:border-gray-300"))
                         :on-click (fn []
                                     (set-pets false)
                                     (persist! false terrain instructions photos))}
                ($ :span "No"))))

       ;; Terrain
       ($ :div
          ($ :label {:class "block text-sm font-medium text-gray-700 mb-2"} "Terrain type")
          ($ :div {:class "grid grid-cols-2 sm:grid-cols-4 gap-2"}
             (for [t terrain-options]
               ($ :button {:key t
                           :class (str "px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors capitalize "
                                       (if (= terrain t)
                                         "border-green-500 bg-green-50 text-green-700"
                                         "border-gray-200 text-gray-600 hover:border-gray-300"))
                           :on-click (fn []
                                       (set-terrain t)
                                       (persist! has-pets t instructions photos))}
                  (clojure.string/replace t "-" " ")))))

       ;; Access Instructions
       ($ ui/textarea {:label "Access instructions (optional)"
                       :value instructions
                       :on-change (fn [v]
                                    (set-instructions v)
                                    (persist! has-pets terrain v photos))
                       :placeholder "Gate code, side entrance, etc."
                       :rows 3}))))
